import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { isMobile } from '@/biz/tool';

export interface ModalShellProps {
  open: boolean;
  mounted: boolean;
  onClose: () => void;
  /** 关闭按钮图标，由宿主从各自 icon 目录注入 */
  closeIcon: React.ReactNode;
  children?: React.ReactNode;
}

const Header: React.FC<{ onClose: () => void; closeIcon: React.ReactNode }> = ({
  onClose,
  closeIcon
}) => (
  <div className="w-full px-6 py-4 border-b border-[#E8E8EC] inline-flex justify-between items-center">
    <div className="justify-start text-black text-xl font-bold leading-relaxed">
      Messages
    </div>
    <div className="w-6 h-6 relative cursor-pointer" onClick={onClose}>
      {closeIcon}
    </div>
  </div>
);

const MOBILE_GAP = 16;
const SHELL_Z_INDEX = 1000;
const TRANSITION = '240ms ease';

/**
 * IM 宿主使用常驻 fixed shell：
 * - 桌面端：全屏面板，从底部滑入
 * - 移动端：底部面板，顶部保留一条上下文边
 * - 关闭时仅通过 transform / opacity 隐藏，不销毁 iframe
 */
const ModalShell: React.FC<ModalShellProps> = ({
  open,
  mounted,
  onClose,
  closeIcon,
  children
}) => {
  const mobile = isMobile();
  const [portalReady, setPortalReady] = useState(false);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    if (!mounted || !open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mounted, open]);

  useEffect(() => {
    if (!mounted || !open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mounted, open, onClose]);

  if (!mounted || !portalReady) return null;

  const panelStyle: React.CSSProperties = mobile
    ? {
        position: 'absolute',
        left: 0,
        right: 0,
        top: MOBILE_GAP,
        bottom: 0,
        backgroundColor: '#fff',
        boxShadow: '0 8px 32px rgba(28, 32, 36, 0.18)',
        transform: open ? 'translateY(0)' : 'translateY(calc(100% + 16px))',
        opacity: open ? 1 : 0,
        transition: `transform ${TRANSITION}, opacity ${TRANSITION}`,
        pointerEvents: open ? 'auto' : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }
    : {
        position: 'absolute',
        inset: 0,
        backgroundColor: '#fff',
        boxShadow: '0 8px 32px rgba(28, 32, 36, 0.18)',
        transform: open ? 'translateY(0)' : 'translateY(calc(100% + 24px))',
        opacity: open ? 1 : 0,
        transition: `transform ${TRANSITION}, opacity ${TRANSITION}`,
        pointerEvents: open ? 'auto' : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      };

  return createPortal(
    <div
      aria-hidden={!open}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: SHELL_Z_INDEX,
        pointerEvents: open ? 'auto' : 'none'
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: open
            ? 'rgba(28, 32, 36, 0.18)'
            : 'rgba(28, 32, 36, 0)',
          transition: `background-color ${TRANSITION}`
        }}
      />
      <section
        role="dialog"
        aria-modal={open}
        aria-label="Messages"
        onClick={(event) => event.stopPropagation()}
        style={panelStyle}
      >
        <Header onClose={onClose} closeIcon={closeIcon} />
        <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      </section>
    </div>,
    document.body
  );
};

export default ModalShell;
